import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Grid,
  CircularProgress,
  Typography,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  ObservationDetails,
  ObservationStatusEnum,
  RaidCategoryEnum,
  RaidItemCreate,
  User,
  getUsers,
} from '../../services/api';
import { getUser } from '../../services/auth';
import { getIsoDate, PartialRaidCommonProps } from './raidFormUtils';

interface ObservationDialogProps extends PartialRaidCommonProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: RaidItemCreate) => void;
  initialData?: ObservationDetails | null;
  itemId?: number | null;
  title?: string;
  readOnly?: boolean;
  isFromOtherQuestionnaire?: boolean;
}

const ObservationDialog: React.FC<ObservationDialogProps> = ({
  open,
  onClose,
  onSubmit,
  initialData,
  itemId,
  title = '',
  readOnly = false,
  isFromOtherQuestionnaire = false,
  initialPartialRaid = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [partialRaid, setPartialRaid] = useState(false);
  // Partial RAID applies only on initial create, never when editing an existing item
  const isCreating = !itemId;
  const effectivePartialRaid = isCreating && partialRaid;
  const [formData, setFormData] = useState<ObservationDetails>({
    observation: '',
    captured_by: '',
    captured_on: '',
    status: ObservationStatusEnum.OPEN,
  });
  const [formTitle, setFormTitle] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await getUsers();
        const filteredUsers = response.data.filter(
          (user: User) =>
            user.display_name !== 'System User' &&
            user.user_name !== 'system_user' &&
            user.status === 'ACTIVE'
        );
        setUsers(filteredUsers);
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };

    if (open) {
      fetchUsers();
    }
  }, [open]);

  const findUserById = (userId: string): User | null => {
    if (!userId) return null;
    return users.find((u) => u.id.toString() === userId) || null;
  };

  const getCapturedByDisplayName = (): string => {
    const user = findUserById(formData.captured_by || '');
    if (user) return user.display_name || formData.captured_by || '';
    const current = getUser();
    if (
      current &&
      formData.captured_by &&
      current.id.toString() === formData.captured_by
    ) {
      return current.display_name || formData.captured_by || '';
    }
    return formData.captured_by || '';
  };

  useEffect(() => {
    if (initialData) {
      setFormData({
        observation: initialData.observation || '',
        captured_by: initialData.captured_by || '',
        captured_on: initialData.captured_on || '',
        status: initialData.status || ObservationStatusEnum.OPEN,
      });
    } else {
      const currentUser = getUser();
      setFormData({
        observation: '',
        captured_by: currentUser ? currentUser.id.toString() : '',
        captured_on: getIsoDate(),
        status: ObservationStatusEnum.OPEN,
      });
    }

    setFormTitle(title || '');
    setPartialRaid(itemId ? false : !!initialPartialRaid);
    setErrors({});
  }, [initialData, title, initialPartialRaid, open, itemId]);

  const handleTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormTitle(event.target.value);
    if (errors.title) {
      setErrors((prev) => ({ ...prev, title: '' }));
    }
  };

  const handleObservationChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData((prev) => ({
      ...prev,
      observation: event.target.value,
    }));
    if (errors.observation) {
      setErrors((prev) => ({ ...prev, observation: '' }));
    }
  };

  const handleSelectChange =
    (field: keyof ObservationDetails) =>
    (event: React.ChangeEvent<{ value: unknown }>) => {
      setFormData((prev) => ({
        ...prev,
        [field]: event.target.value,
      }));
    };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formTitle.trim()) {
      newErrors.title = 'Title is required';
    }
    if (!formData.observation.trim()) {
      newErrors.observation = 'Observation is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);

      const details: ObservationDetails = {
        observation: formData.observation,
        captured_by: formData.captured_by || undefined,
        captured_on: formData.captured_on || undefined,
        status: formData.status,
      };

      const submitData: RaidItemCreate = {
        entity_id: 0,
        title: formTitle,
        raid_category: RaidCategoryEnum.OBSERVATION,
        partial_raid: effectivePartialRaid,
        details,
      };

      await onSubmit(submitData);
      onClose();
    } catch (error) {
      console.error('Error submitting observation item:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {readOnly
          ? 'View Observation'
          : itemId
            ? 'Update Observation'
            : 'Add New Observation'}
        {loading && (
          <CircularProgress
            size={20}
            sx={{ marginLeft: 2, verticalAlign: 'middle' }}
          />
        )}
      </DialogTitle>

      <DialogContent dividers>
        {readOnly && (
          <Typography color="info.main" variant="subtitle2" sx={{ mb: 2 }}>
            {isFromOtherQuestionnaire
              ? 'This item is from other reviews and is displayed in read-only mode.'
              : 'This review is submitted. RAID details are in read-only mode.'}
          </Typography>
        )}

        {isCreating && (
        <FormControlLabel
          control={
            <Switch
              checked={partialRaid}
              onChange={(e) => setPartialRaid(e.target.checked)}
              disabled={readOnly}
            />
          }
          label="Partial RAID (show only required fields)"
          sx={{ mb: 1 }}
        />
        )}

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField
              label="Title"
              value={formTitle}
              onChange={handleTitleChange}
              fullWidth
              required
              error={!!errors.title}
              helperText={
                errors.title || 'Brief title for the observation'
              }
              InputProps={{ readOnly }}
              disabled={readOnly}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Observation"
              value={formData.observation}
              onChange={handleObservationChange}
              multiline
              rows={4}
              fullWidth
              required
              error={!!errors.observation}
              helperText={
                errors.observation ||
                'Notes captured as part of review preparation'
              }
              InputProps={{ readOnly }}
              disabled={readOnly}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Captured By"
              value={getCapturedByDisplayName()}
              fullWidth
              InputProps={{ readOnly: true }}
              disabled
              helperText="User who entered the observation"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Captured On"
              type="date"
              value={formData.captured_on || ''}
              InputLabelProps={{ shrink: true }}
              fullWidth
              InputProps={{ readOnly: true }}
              disabled
            />
          </Grid>

          {!effectivePartialRaid && (
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth disabled={readOnly}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status || ObservationStatusEnum.OPEN}
                  label="Status"
                  onChange={handleSelectChange('status') as any}
                  readOnly={readOnly}
                  inputProps={{ readOnly }}
                >
                  {Object.values(ObservationStatusEnum).map((status) => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>{readOnly ? 'Close' : 'Cancel'}</Button>
        {!readOnly && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={loading}
          >
            {itemId ? 'Update' : 'Save'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ObservationDialog;
